import { CdkDrag } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    inject,
    Input,
    input,
    InputSignal,
    OnDestroy,
    OnInit,
    signal,
    Signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { IqRoutesRegisterService } from '@dat/iq-routes';

import {
    EventInfo,
    EventQuery,
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
    IqEventBusNode,
    IqEventBusTransformationService,
    matchQuery,
} from '@dat/iq-event-bus';

import {
    IqBuilderLabelComponent,
    IqCssClassProcessorDirective,
    StyleActionsDirective,
} from '@dat/iq-style-processor';
import { camelCase, get, omit } from 'lodash';
import { isEmpty } from 'ramda';

import { IqBuilderSectionComponent } from '../iq-builder-section/iq-builder-section.component';
import {
    Dashboard,
    RouteInfo,
    Section,
    TriggerEvent,
    BUILDER_AUDIT_TOOLS,
} from '../models';
import {
    IdBuilderEventCacheService,
    IqBuilderStateRulesService,
    IqBuilderStateService,
} from '../state';
import { IqBuilderDashboardRegisterDirective } from './iq-builder-dashboard-register.directive';
import { MatExpansionModule } from '@angular/material/expansion';
import { IqBuilderAuditToolsComponent } from '../iq-builder-audit-tools/iq-builder-audit-tools.component';
import { IqBuilderAuditToolsService } from '../iq-builder-audit-tools/iq-builder-audit-tools.service';
import { IqTrackingRegisterComponent } from '@dat/iq-tracking';

type DashboardEvaluatorSuccessPayload = EventInfo & {
    data: {
        [key: string]: any;
        dashboardId: string;
        type: RuleResponseTypes;
    };
};

type RuleResponseTypes = 'ACTION';

@Component({
    selector: 'iq-builder-dashboard',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        CommonModule,
        IqBuilderSectionComponent,
        IqCssClassProcessorDirective,
        StyleActionsDirective,
        MatExpansionModule,
        IqBuilderLabelComponent,
        IqBuilderAuditToolsComponent,
        IqBuilderDashboardRegisterDirective,
        IqTrackingRegisterComponent,
        CdkDrag,
    ],
    templateUrl: './iq-builder-dashboard.component.html',
    styleUrl: './iq-builder-dashboard.component.scss',
})
export class IqBuilderDashboardComponent implements OnInit, OnDestroy {
    private _deregister: () => void;
    private readonly _eventBroker: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    private _routeRegister: IqRoutesRegisterService = inject(IqRoutesRegisterService);

    private _auditToolService = inject(IqBuilderAuditToolsService);

    private _loadRef: boolean = false;

    useAuditTools: boolean = inject<boolean>(BUILDER_AUDIT_TOOLS, {
        optional: true,
    });
    showAuditTools: WritableSignal<boolean> = signal(false);

    openedSections: Signal<Record<string, boolean>> = signal({});

    private readonly _eventCache: IdBuilderEventCacheService = inject(
        IdBuilderEventCacheService,
    );

    private readonly _state: IqBuilderStateService = inject(
        IqBuilderStateService,
    );

    private readonly _builderRules: IqBuilderStateRulesService = inject(
        IqBuilderStateRulesService,
    );
    private readonly _transform: IqEventBusTransformationService = inject(
        IqEventBusTransformationService,
    );

    private readonly _routeInfo: WritableSignal<RouteInfo> = signal<RouteInfo>({
        analyticsPageName: '',
        data: {},
        locked: false,
        pageTitle: '',
        params: {},
        path: '',
        loaded: false,
    });

    dashboardStateSignal: Signal<Dashboard> = signal({} as Dashboard);
    dashboardState: Signal<Dashboard> = signal({} as Dashboard);

    rendered: WritableSignal<boolean> = signal(false);
    private _registeredAction: Record<string, IqEventBusBrokerHandler>;
    constructor() {
        this._deregister = this._eventBroker.watchHandledEvents(
            (node: IqEventBusNode) => {
                const dashboard: Dashboard = this.dashboardState?.();

                if (!dashboard) return;

                untracked(() => {
                    this._eventCache.cache(
                        `${dashboard.id}:${Date.now()}`,
                        node.evt,
                        (dashboard.eventCacheRules ?? []) as EventQuery[],
                    );
                });

                if (!node.evt?.name?.startsWith('type:')) return;

                const eventName = camelCase(
                    node.evt.name.replace('type', 'on'),
                );

                const actionArray = this.dashboardState()?.[eventName] ?? [];

                if (!actionArray.length) return;

                const templateData = this.getTemplateData(node.evt.payload);

                this.useAuditTools
                    ? this._auditToolService.addPrerenderedAction({
                          id: node.id,
                          template: JSON.stringify(actionArray, null, 4),
                          data: templateData,
                          eventName,
                      })
                    : null;
                const filteredActions: TriggerEvent[] =
                    actionArray
                        ?.filter?.((action: TriggerEvent) =>
                            !isEmpty(action.sourceEventRule)
                                ? matchQuery(
                                      node.evt,
                                      this._transform.renderJson(
                                          JSON.stringify(
                                              action.sourceEventRule ?? {},
                                              null,
                                              4,
                                          ),
                                          {
                                              ...templateData,
                                              dashboardId: dashboard.id,
                                          },
                                          {},
                                          (
                                              err: any,
                                              template: string,
                                              data: any,
                                          ) => {
                                              this._logFilterActionsErrors(
                                                  eventName,
                                                  node,
                                                  err,
                                                  template,
                                                  data,
                                              );
                                              throw err;
                                          },
                                      ) ?? {},
                                      action.sourceEventRuleOperator ?? 'AND',
                                  )
                                : true,
                        )
                        ?.map((x) => omit(x, ['sourceEventRule'])) ?? [];

                if (isEmpty(filteredActions)) return;

                const renderedActions = this._transform
                    .renderJson(
                        JSON.stringify(filteredActions, null, 4),
                        { ...templateData, dashboardId: dashboard.id },
                        {},
                        (err: any, template: string, data: any) => {
                            this._logRenderedErrors(
                                eventName,
                                node,
                                err,
                                template,
                                data,
                            );
                        },
                    )
                    .map((event: EventInfo): EventInfo => {
                        return {
                            ...event,
                            parentId: node.id,
                            meta: {
                                dashboardId: this.dashboardState().id,
                            },
                        };
                    });

                if (!renderedActions?.length) return;

                this._state.processActions(renderedActions, {
                    ...templateData,
                    dashboardId: dashboard.id,
                    type: 'ACTION' as RuleResponseTypes,
                });
            },
        );

        effect(() => {
            if(this._loadRef) return;
            if (!this.stateInitialized()) return;

            const dashboard = this.dashboardState?.();

            const loaded = dashboard?.loaded;

            if (!loaded) return;

            untracked(async () => {
                setTimeout(async () => {
                    this._state.registerDashboadActions();
                    this._builderRules.register();
                    this._registeredAction = this._eventBroker.registerMap({
                        'type:rules:evaluator:run:success': {
                            callback: (
                                eventInfo: DashboardEvaluatorSuccessPayload,
                            ) => {
                                this._runRules(eventInfo);
                            },
                        },
                        'type:route:changed': {
                            callback: (routeInfo: RouteInfo) => {
                                this._routeInfo.set({
                                    ...routeInfo,
                                    loaded: true,
                                });
                            },
                        },
                    });
                    /**
                     * This dispatches an event indicating the dashboard is loaded.
                     *
                     * In the settings for the Dashboard, Section, or Component, you
                     * will be able to leverage the dashboard information by adding this:
                     *
                     * ```json
                     * {
                     *  "onDashboardLoad": [
                     *    {
                     *      "name": "EVENT_NAME",
                     *      "payload": {},
                     *      "sourceEventRule": {
                     *          "payload": [
                     *            {
                     *                "path": "id",
                     *                "operator": "eq",
                     *                "value": "ADD_DASHBOARD_ID_HERE"
                     *            }
                     *          ]
                     *      },
                     *      "rules": []
                     *    }
                     *   ]
                     * }
                     * ```
                     */
                    const dispatch = () =>
                        this._eventBroker.dispatch({
                            name: 'type:dashboard:load',
                            payload: {
                                id: dashboard.id,
                                dashboard,
                                route: this._routeInfo(),
                            },
                        });

                    setTimeout(async () => {
                        await this._eventCache.fireEvents(
                            this.dashboardState().id,
                        );

                        dispatch();
                    });
                }, 200);
            });

            this._loadRef = true;
        });
    }

    loaded: Signal<boolean> = computed(() => {
        return this.dashboardState?.()?.loaded;
    });

    hasSectionGroups: Signal<boolean> = computed(
        () => !!this.dashboardState().sectionGroups?.length,
    );

    sectionGroups: Signal<Map<string, Section[]>> = computed(() => {
        if (!this.hasSectionGroups()) return new Map();

        const groups = new Map<string, Section[]>();
        const dashboard = this.dashboardState?.();

        dashboard.sectionGroups?.forEach((group) => {
            const sections: Section[] = dashboard.sections.filter(
                (section) => section.groupId === group.id,
            ) as Section[];
            groups.set(group.id, sections);
        });

        return groups;
    });

    @Input()
    id: string;

    dashboard: InputSignal<Dashboard> = input({ id: '', sections: [] });

    stateInitialized: WritableSignal<boolean> = signal(false);

    private _logFilterActionsErrors(
        eventName: string,
        node: IqEventBusNode,
        err: any,
        template: string,
        data: any,
    ) {
        this.useAuditTools
            ? this._auditToolService.addFilterActionsError({
                  id: node.id,
                  template,
                  data,
                  eventName,
                  error: err,
              })
            : null;

        throw err;
    }
    private _logRenderedErrors(
        eventName: string,
        node: IqEventBusNode,
        err: any,
        template: string,
        data: any,
    ) {
        this.useAuditTools
            ? this._auditToolService.addRenderedActionError({
                  id: node.id,
                  template,
                  data,
                  eventName,
                  error: err,
              })
            : null;

        throw err;
    }

    // Call this in the OnInit Function
    private _setDashboardStateSignal(): void {
        this.dashboardStateSignal = this._state.dashboardState(
            this.dashboard(),
        );
        this.initializeSignals();
    }

    async ngOnInit(): Promise<void> {
        /**
         * This dispatches an event when the dashboard renders.
         *
         * It will send out any information that is loaded on initial load
         *
         * ```json
         * {
         *      "onDashboardBeforeLoad": [
         *        {
         *          "name": "EVENT_NAME",
         *          "payload": {},
         *          "sourceEventRule": {
         *              "payload": [
         *                {
         *                  "path": "id",
         *                  "operator": "eq",
         *                  "value": "ADD_DASHBOARD_ID_HERE"
         *                 }
         *              ]
         *          },
         *          "rules": []
         *        }
         *      ]
         * }
         * ```
         */
        const dispatch = () =>
            this._eventBroker.dispatch({
                name: 'type:dashboard:before-load',
                payload: {
                    dashboard: this.dashboard,
                    id: this.id,
                    route: this._routeInfo(),
                },
            });

        await this._eventCache.initialize();
        this._setDashboardStateSignal();
        dispatch();
    }

    initializeSignals() {
        this.dashboardState = computed(() => this.dashboardStateSignal());

        this.loaded = computed(() => this.dashboardState()?.loaded);

        this.stateInitialized.set(true);
    }

    private getTemplateData($event: any) {
        return {
            ...$event,
            cache: { ...this.dashboardState().cache },
            dashboardCache: { ...this.dashboardState().cache },
            route: this._routeInfo(),
        };
    }

    sectionOpen(section: Section) {
        /**
         * This dispatches an event when a toggable section opens.
         *
         * It will send out any information that is loaded on initial load
         *
         * ```json
         * {
         *      "onSectionOpen": [
         *        {
         *          "name": "EVENT_NAME",
         *          "payload": {},
         *          "sourceEventRule": {
         *              "payload": [
         *                {
         *                  "path": "id",
         *                  "operator": "eq",
         *                  "value": "ADD_SECTION_ID_HERE"
         *                 }
         *              ]
         *          },
         *          "rules": []
         *        }
         *      ]
         * }
         * ```
         */
        const dispatch = () =>
            this._eventBroker.dispatch({
                name: 'type:section:open',
                payload: {
                    id: section.id,
                    section,
                    dashboardId: this.dashboardState().id,
                    dashboardCache: this.dashboardState().cache,
                    route: this._routeInfo(),
                },
            });

        dispatch();
    }
    sectionClose(section: Section) {
        /**
         * This dispatches an event when a toggable section closes.
         *
         * It will send out any information that is loaded on initial load
         *
         * ```json
         * {
         *      "onSectionClose": [
         *        {
         *          "name": "EVENT_NAME",
         *          "payload": {},
         *          "sourceEventRule": {
         *              "payload": [
         *                {
         *                  "path": "id",
         *                  "operator": "eq",
         *                  "value": "ADD_SECTION_ID_HERE"
         *                 }
         *              ]
         *          },
         *          "rules": []
         *        }
         *      ]
         * }
         * ```
         */
        const dispatch = () =>
            this._eventBroker.dispatch({
                name: 'type:section:close',
                payload: {
                    id: section.id,
                    section,
                    dashboardId: this.dashboardState().id,
                    dashboardCache: this.dashboardState().cache,
                    route: this._routeInfo(),
                },
            });

        dispatch();
    }

    ngOnDestroy() {
        const dashboard = this.dashboardState();
        /**
         * This dispatches an event indicating the dashboard is unloaded.
         *
         * In the settings for the Dashboard, Section, or Component, you
         * will be able to leverage the dashboard information by adding this:
         *
         * ```json
         * {
         *      "onDashboardUnload": [
         *        {
         *          "name": "EVENT_NAME",
         *          "payload": {},
         *          "sourceEventRule": {
         *              "payload": [
         *                {
         *                  "path": "dashboard.id",
         *                  "operator": "eq",
         *                  "value": "ADD_DASHBOARD_ID_HERE"
         *                 }
         *              ]
         *          },
         *          "rules": []
         *        }
         *      ]
         * }
         * ```
         */
        const dispatch = () =>
            this._eventBroker.dispatch({
                name: 'type:dashboard:unload',
                payload: {
                    dashboard,
                    route: this._routeInfo(),
                },
            });

        dispatch();

        this._deregister();
        this._eventBroker.unregisterMap(this._registeredAction);
    }

    toggleAuditTools() {
        const show = this.showAuditTools();

        this.showAuditTools.set(!show);
    }

    private _runRules(eventInfo: DashboardEvaluatorSuccessPayload) {
        if (get(eventInfo, 'data.dashboardId') !== this.dashboardState().id)
            return;

        const response: Record<RuleResponseTypes, () => any> = {
            ACTION: () => {
                this._eventBroker.dispatch(eventInfo);
            },
        };

        if (!response[eventInfo.data.type]) return;

        response[eventInfo.data.type]?.();
    }
}
